// src/shared/zod-compat.ts
//
// zod 4 の既定エラーメッセージを zod 3 (3.25.x の en errorMap) と同じ文言に戻す。
//
// ■ なぜ要るか
//   API は 400 の応答に `details: parsed.error.flatten()` をそのまま返している (約 240 箇所)。
//   flatten() の **形** ({ formErrors, fieldErrors }) は zod 4 でも同じだが、 中に入る **文言** は
//   zod 4 で全面的に変わった ("Required" → "Invalid input: expected string, received undefined" 等)。
//   client / 既存の結合テスト / 外部の呼び出し側は zod 3 の文言を前提にしているので、
//   zod 4 の issue を zod 3 の文言に写す errorMap を **全体設定** (z.config) で入れる。
//
// ■ 使い方
//   副作用 import で 1 回だけ設定する。 Worker の入口 (src/server/index.ts) と
//   SPA の入口 (src/client/main.tsx) の **先頭** で `import "../shared/zod-compat";` する。
//   z.config は zod の module 内の全体設定なので、 同じ bundle の全 schema に効く。
//
// ■ 優先順位 (zod 4 の仕様)
//   schema に書いた message / error ({ error: "..." } / .min(1, "...") / refine の message) が最優先で、
//   この errorMap は **既定の文言** だけを置き換える。 日本語の個別メッセージはそのまま出る。
//
// ■ 文言の正本
//   zod 3.25.76 の `v3/locales/en.js`。 tools/verify/verify-zod-error-compat.mjs が
//   zod 3 で実際に出た結果 (tools/verify/zod-compat/expected.json) と 1 文字ずつ突き合わせる。
//
// ■ 文言以外に zod 3 へ揃えるもの (check の挙動、 下の patchCheck)
//   zod 4 は文言だけでなく、 次の 3 点で **出る issue の数・受け入れる値** が zod 3 と違う。
//   応答 (details の中身) と受け入れ範囲を変えないため、 check の生成時に zod 3 の挙動へ戻す。
//   1. 長さの check (.min / .max / .length / .nonempty) が **型の違う値にも走る**
//      (z.string().min(1) に [] を送ると "Expected string" に加えて "Array must contain..." が出る)。
//      → 自分の型検査が落ちた値には走らせない (zod 3 と同じ)
//   2. 文字列の長さを **code point** で数える (zod 3 は UTF-16 の単位 = String.length)。
//      → 文字列は String.length で数える (サロゲートペアの文字で上限が実質 2 倍にならないように)
//   3. .int() に小数が来ると **そこで打ち切る** (後ろの .min() / refine が走らない) うえ、
//      安全な整数の範囲外を新たに拒否する。 → zod 3 と同じく「整数でない」を出したうえで続け、 範囲は見ない
//   仕組み: zod 4 core が生成した全 instance を渡す全体の post-processor (globalThis.__zod_globalConfig.postProcessor)。
//   zod の内部の hook なので、 版を上げたら verify-zod-error-compat.mjs で必ず確かめる (効かなくなれば NG になる)。

import { z } from "zod";

/** zod 3 の ZodParsedType と同じ分類 (getParsedType) */
function parsedType3(data: unknown): string {
  switch (typeof data) {
    case "undefined":
      return "undefined";
    case "string":
      return "string";
    case "number":
      return Number.isNaN(data) ? "nan" : "number";
    case "boolean":
      return "boolean";
    case "function":
      return "function";
    case "bigint":
      return "bigint";
    case "symbol":
      return "symbol";
    case "object": {
      if (Array.isArray(data)) return "array";
      if (data === null) return "null";
      const d = data as { then?: unknown; catch?: unknown };
      if (d.then && typeof d.then === "function" && d.catch && typeof d.catch === "function") return "promise";
      if (typeof Map !== "undefined" && data instanceof Map) return "map";
      if (typeof Set !== "undefined" && data instanceof Set) return "set";
      if (typeof Date !== "undefined" && data instanceof Date) return "date";
      return "object";
    }
    default:
      return "unknown";
  }
}

/** zod 3 の util.joinValues (文字列だけ '...' で囲む) */
function joinValues3(values: readonly unknown[], separator = " | "): string {
  return values.map((v) => (typeof v === "string" ? `'${v}'` : String(v))).join(separator);
}

/** zod 4 の string format 名 → zod 3 の invalid_string.validation 名 */
const FORMAT_TO_V3: Record<string, string> = {
  guid: "uuid",
  uuidv4: "uuid",
  uuidv6: "uuid",
  uuidv7: "uuid",
  ipv4: "ip",
  ipv6: "ip",
  cidrv4: "cidr",
  cidrv6: "cidr",
};

/** zod 4 の expected 名 → zod 3 の expected 名 (同名は省略) */
const EXPECTED_TO_V3: Record<string, string> = {
  record: "object",
  tuple: "array",
};

// zod 4 の raw issue は code ごとに別の型だが、 ここでは読むだけなので緩く受ける
type RawIssue = {
  code?: string;
  input?: unknown;
  expected?: string;
  format?: string;
  origin?: string;
  minimum?: unknown;
  maximum?: unknown;
  inclusive?: boolean;
  exact?: boolean;
  values?: unknown[];
  keys?: string[];
  options?: unknown[];
  note?: string;
  divisor?: unknown;
  prefix?: string;
  suffix?: string;
  includes?: string;
  position?: number;
  issues?: Array<{ message?: string }>;
  inst?: { _zod?: { def?: { type?: string } } };
};

function sizeMessage(kind: "too_small" | "too_big", iss: RawIssue): string | undefined {
  const small = kind === "too_small";
  const bound = small ? iss.minimum : iss.maximum;
  const origin = iss.origin;
  if (origin === "array") {
    const adj = iss.exact ? "exactly" : iss.inclusive ? (small ? "at least" : "at most") : small ? "more than" : "less than";
    return `Array must contain ${adj} ${String(bound)} element(s)`;
  }
  if (origin === "string") {
    const adj = iss.exact ? "exactly" : iss.inclusive ? (small ? "at least" : "at most") : small ? "over" : "under";
    return `String must contain ${adj} ${String(bound)} character(s)`;
  }
  if (origin === "number" || origin === "int" || (origin === "bigint" && small)) {
    if (small) {
      const adj = iss.exact ? "exactly equal to " : iss.inclusive ? "greater than or equal to " : "greater than ";
      return `Number must be ${adj}${String(bound)}`;
    }
    const adj = iss.exact ? "exactly" : iss.inclusive ? "less than or equal to" : "less than";
    return `Number must be ${adj} ${String(bound)}`;
  }
  if (origin === "bigint") {
    const adj = iss.exact ? "exactly" : iss.inclusive ? "less than or equal to" : "less than";
    return `BigInt must be ${adj} ${String(bound)}`;
  }
  if (origin === "date") {
    const d = new Date(Number(bound instanceof Date ? bound.getTime() : bound));
    if (small) {
      const adj = iss.exact ? "exactly equal to " : iss.inclusive ? "greater than or equal to " : "greater than ";
      return `Date must be ${adj}${d}`;
    }
    const adj = iss.exact ? "exactly" : iss.inclusive ? "smaller than or equal to" : "smaller than";
    return `Date must be ${adj} ${d}`;
  }
  return "Invalid input";
}

/** zod 4 の raw issue を zod 3 の既定文言に写す。 写せないものは undefined (= zod 4 の既定文言に任せる) */
export function zod3CompatMessage(iss: RawIssue): string | undefined {
  switch (iss.code) {
    case "invalid_type": {
      if (iss.input === undefined) return "Required";
      // .int() に小数: zod 3 は expected "integer" / received "float"
      if (iss.expected === "int" && typeof iss.input === "number" && !Number.isNaN(iss.input)) {
        return "Expected integer, received float";
      }
      // 不正な Date: zod 3 は invalid_date
      if (iss.expected === "date" && iss.input instanceof Date) return "Invalid date";
      // ±Infinity は zod 3 では number として通っていた (zod 4 は型の段階で拒否する)。
      // zod 3 で無限大を拒否するときの文言 (.finite() の not_finite) に寄せる
      if (iss.expected === "number" && typeof iss.input === "number" && !Number.isNaN(iss.input)) return "Number must be finite";
      const expected = iss.expected === "int" ? "integer" : (EXPECTED_TO_V3[iss.expected ?? ""] ?? iss.expected);
      return `Expected ${expected}, received ${parsedType3(iss.input)}`;
    }
    case "invalid_value": {
      const values = iss.values ?? [];
      const type = iss.inst?._zod?.def?.type;
      if (type === "literal") {
        // zod 3 の ZodLiteral は型を見ずに値だけ比べる (undefined でも "Required" にならない)
        return `Invalid literal value, expected ${JSON.stringify(values[0])}`;
      }
      // enum: zod 3 は文字列以外を invalid_type として扱う
      if (iss.input === undefined) return "Required";
      if (type === "enum" && typeof iss.input !== "string") {
        return `Expected ${joinValues3(values)}, received ${parsedType3(iss.input)}`;
      }
      return `Invalid enum value. Expected ${joinValues3(values)}, received '${String(iss.input)}'`;
    }
    case "unrecognized_keys":
      return `Unrecognized key(s) in object: ${joinValues3(iss.keys ?? [], ", ")}`;
    case "invalid_union":
      if (Array.isArray(iss.options) && iss.options.length > 0) {
        return `Invalid discriminator value. Expected ${joinValues3(iss.options)}`;
      }
      return "Invalid input";
    case "invalid_format": {
      const f = iss.format ?? "";
      if (f === "regex") return "Invalid";
      if (f === "starts_with") return `Invalid input: must start with "${iss.prefix}"`;
      if (f === "ends_with") return `Invalid input: must end with "${iss.suffix}"`;
      if (f === "includes") {
        const base = `Invalid input: must include "${iss.includes}"`;
        return typeof iss.position === "number"
          ? `${base} at one or more positions greater than or equal to ${iss.position}`
          : base;
      }
      // lowercase / uppercase 等は zod 3 に無い check なので zod 4 に任せる
      if (f === "lowercase" || f === "uppercase" || f === "json_string" || f === "template_literal") return undefined;
      return `Invalid ${FORMAT_TO_V3[f] ?? f}`;
    }
    case "too_small":
      return sizeMessage("too_small", iss);
    case "too_big":
      return sizeMessage("too_big", iss);
    case "not_multiple_of":
      return `Number must be a multiple of ${String(iss.divisor)}`;
    case "invalid_key":
      // zod 3 は key schema の issue をそのまま key の path に積んでいた。 zod 4 は 1 件にまとめるので先頭の文言を使う
      return iss.issues?.[0]?.message ?? "Invalid input";
    case "custom":
      return "Invalid input";
    default:
      return undefined;
  }
}

z.config({ customError: (iss) => zod3CompatMessage(iss as unknown as RawIssue) });

// ---------------------------------------------------------------------------------------------
// check の挙動を zod 3 に揃える (上の「文言以外に zod 3 へ揃えるもの」)

type PayloadIssue = { code?: string; path?: PropertyKey[]; continue?: boolean; [k: string]: unknown };
type Payload = { value: unknown; issues: PayloadIssue[] };
type CheckDef = {
  when?: (p: Payload) => boolean;
  minimum?: number;
  maximum?: number;
  length?: number;
  format?: string;
  abort?: boolean;
};
type ZodInstance = { _zod?: { traits?: Set<string>; def?: CheckDef; check?: (p: Payload) => unknown } };

/** この schema 自身の型検査が落ちている (= 自分の値に対する invalid_type が path なしで積まれている) */
function ownTypeFailed(p: Payload): boolean {
  return p.issues.some((i) => i.code === "invalid_type" && !(i.path && i.path.length));
}

export function patchCheck(inst: ZodInstance): void {
  const zi = inst._zod;
  const traits = zi?.traits;
  const def = zi?.def;
  if (!zi || !traits || !def || typeof zi.check !== "function") return;
  const orig = zi.check;
  const isMin = traits.has("$ZodCheckMinLength");
  const isMax = traits.has("$ZodCheckMaxLength");
  const isLen = traits.has("$ZodCheckLengthEquals");
  if (isMin || isMax || isLen) {
    // 1. 型の違う値には走らせない
    const when = def.when;
    def.when = (p) => (when ? when(p) : true) && !ownTypeFailed(p);
    // 2. 文字列は String.length (UTF-16 の単位) で数える
    zi.check = (p) => {
      const v = p.value;
      if (typeof v !== "string") { orig(p); return; }
      const units = v.length;
      const base = { origin: "string", inclusive: true, input: v, inst, continue: !def.abort };
      if (isMin) {
        if (units >= (def.minimum ?? 0)) return;
        p.issues.push({ ...base, code: "too_small", minimum: def.minimum } as PayloadIssue);
      } else if (isMax) {
        if (units <= (def.maximum ?? Infinity)) return;
        p.issues.push({ ...base, code: "too_big", maximum: def.maximum } as PayloadIssue);
      } else {
        if (units === def.length) return;
        p.issues.push(
          (units > (def.length ?? 0)
            ? { ...base, exact: true, code: "too_big", maximum: def.length }
            : { ...base, exact: true, code: "too_small", minimum: def.length }) as PayloadIssue,
        );
      }
    };
    return;
  }
  if (traits.has("$ZodCheckNumberFormat") && def.format?.includes("int")) {
    // 3. 整数なら (安全な範囲の外でも) 通す。 小数は「整数でない」を出したうえで後ろの check を続ける
    zi.check = (p) => {
      if (Number.isInteger(p.value)) return;
      const n = p.issues.length;
      const r = orig(p);
      for (let i = n; i < p.issues.length; i++) {
        const iss = p.issues[i];
        if (iss && iss.code === "invalid_type") iss.continue = true;
      }
      return r;
    };
  }
}

{
  const g = globalThis as unknown as { __zod_globalConfig?: { postProcessor?: (inst: unknown) => void } };
  const cfg = (g.__zod_globalConfig ??= {});
  const prev = cfg.postProcessor;
  cfg.postProcessor = (inst) => {
    prev?.(inst);
    patchCheck(inst as ZodInstance);
  };
}
